/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { DateTime, Interval } from 'luxon'
import _ from 'lodash'

import { ClientWebview } from '../redux/configuration'

export default function useClientConfigNormalizer() {
  const { t } = useTranslation()

  const validations = useMemo(
    () => ({
      invalidDate: () => (value: any) => {
        return value?.invalid && t('client.warnings.invalidDate')
      },
      greaterThanNow: () => (value: any) => {
        if (DateTime.fromISO(value) < DateTime.now()) {
          return null
        }
        return t('client.warnings.greaterThanNow')
      },
      lessThanNowByYears: (years: number) => (value: any) => {
        if (Interval.fromDateTimes(DateTime.fromISO(value), DateTime.now()).length('years') >= years) {
          return null
        }
        return t('client.warnings.lessThanNowByYears', { years })
      },
      minLengthValidate: (min: number) => (value: string) => {
        return new RegExp(`.{${min},}`).test(value) || t('client.warnings.minLength', { min })
      },
      numbersValidate: () => (value: string) => {
        return /(?=.*[a-zA-Z])(?=.*[0-9])/.test(value) || t('client.warnings.numbers')
      },
      specialCharValidate: () => (value: string) => {
        return /(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?])/.test(value) || t('client.warnings.specialChar')
      },
      lowerUpperValidate: () => (value: string) => {
        return /(?=.*[a-z])(?=.*[A-Z])/.test(value) || t('client.warnings.lowerUpper')
      },
    }),
    [t]
  )

  const clientConfigNormalizer = useCallback(
    (clientWebview = {} as ClientWebview) => {
      const { steps, ...config } = clientWebview
      return {
        ...config,
        steps: _.mapValues(steps, ({ elements, ...restStep }) => ({
          ...restStep,
          elements: elements.map((element: any) => {
            const { fields, ...restElement } = element
            if (!fields) {
              return element
            }

            return {
              ...restElement,
              fields: fields.map((field: any) => {
                const { rules, ...restField } = field
                if (!rules?.pattern && !rules?.validate) {
                  return field
                }

                const { pattern, validate, ...restRules } = rules
                const validateIsArray = Array.isArray(validate)

                const patternNormalizer = () => {
                  if (!pattern) {
                    return null
                  }

                  const { value } = pattern
                  return {
                    ...pattern,
                    value: new RegExp(value),
                  }
                }

                const validateNormalizer = () => {
                  if (!validate) {
                    return null
                  }

                  if (validateIsArray) {
                    const [func, param] = validate
                    return { [func]: validations[func as keyof typeof validations](param) }
                  }
                  return _.mapValues(validate, (value: any) => {
                    const [func, param] = value
                    return validations[func as keyof typeof validations](param)
                  })
                }
                return {
                  ...restField,
                  rules: {
                    ...restRules,
                    pattern: patternNormalizer(),
                    validate: validateNormalizer(),
                  },
                }
              }),
            }
          }),
        })),
      }
    },
    [validations]
  )

  return {
    clientConfigNormalizer,
  }
}
